import {
  Route,
  Router,
  Params,
  ActivatedRouteSnapshot,
  CanActivateFn,
  RedirectFunction,
} from '@angular/router';
import { ShellComponent } from './shell.component.js';
import { HomeComponent } from '../pages/home/home.component.js';
import { inject } from '@angular/core';
import { Auth, user } from '@angular/fire/auth';
import { map, take } from 'rxjs';
import { TRACKING_PARAM_KEYS } from '@gains-com/shared';
import { killSwitchGuard } from '../auth/feature.guard.js';
import { merchModeGuard } from '../auth/merch-mode.guard.js';

/** Only preserve tracking-related params, not sensitive ones like OAuth tokens */
const PRESERVED_PARAM_KEYS = [
  ...TRACKING_PARAM_KEYS,
  'fbclid',
  // Home stashes these to localStorage for signup to read, so dropping them on
  // the redirect is what loses the referral for a logged-out visitor who lands
  // on a guarded route.
  'ref',
  'cvref',
];

const filterTrackingParams = (params: Params): Params => {
  const filtered: Params = {};
  for (const key of PRESERVED_PARAM_KEYS) {
    const value = params[key];
    // A repeated param (`?ref=a&ref=b`) arrives as an array, and the consumers
    // downstream of the redirect treat every one of these as a string — signup
    // calls `.trim()` on the referral code. Forward the first usable entry so
    // the redirect only ever emits scalars.
    const single = Array.isArray(value) ? value.find((entry) => !!entry) : value;
    if (single) {
      filtered[key] = single;
    }
  }
  return filtered;
};

/**
 * Auth guard that redirects unauthorized users to home while preserving UTM tracking params.
 */
const authGuardPreservingParams: CanActivateFn = (
  route: ActivatedRouteSnapshot
) => {
  const router = inject(Router);
  const auth = inject(Auth);

  return user(auth).pipe(
    take(1),
    map((firebaseUser) => {
      if (firebaseUser) {
        return true;
      }
      // Preserve only tracking params when redirecting to home
      const trackingParams = filterTrackingParams(route.queryParams);
      return router.createUrlTree([''], { queryParams: trackingParams });
    })
  );
};

/**
 * `/shop` is no longer a page: the shop is a dialog over the lobby, so the
 * route always redirects to `/?acn=shop` and HomeComponent's acn dispatcher
 * opens it — the dialog itself for a signed-in visitor, a login prompt that
 * replays the action after sign-in for a signed-out one. Deliberately
 * state-independent: the same redirect runs on the SSR server, which cannot
 * see the browser's Firebase session.
 */
const shopModalRedirect: RedirectFunction = ({ queryParams }) =>
  inject(Router).createUrlTree([''], { queryParams: { ...filterTrackingParams(queryParams), acn: 'shop' } });

// TODO: Refactor to modular routes files
export const shellRouteClient: Route = {
  path: '',
  pathMatch: 'prefix',
  component: ShellComponent,
  children: [
    {
      path: '',
      pathMatch: 'full',
      component: HomeComponent,
    },
    {
      path: 'settings',
      pathMatch: 'prefix',
      loadComponent: () =>
        import('../pages/settings/settings.component').then(
          (c) => c.SettingsComponent
        ),
      canActivate: [authGuardPreservingParams],
      children: [
        {
          path: 'profile',
          pathMatch: 'full',
          loadComponent: () =>
            import('../pages/settings/profile/account-profile.component').then(
              (c) => c.AccountProfileComponent
            ),
        },
        {
          path: 'security',
          pathMatch: 'full',
          loadComponent: () =>
            import(
              '../pages/settings/security/account-security.component'
            ).then((c) => c.AccountSecurityComponent),
        },
        {
          path: 'self-exclusion',
          pathMatch: 'full',
          loadComponent: () =>
            import('../pages/settings/account/account-settings.component').then(
              (c) => c.AccountSettingsComponent
            ),
        },
        {
          path: 'payment-methods',
          pathMatch: 'full',
          loadComponent: () =>
            import('../pages/settings/payment-methods/payment-methods.component').then(
              (c) => c.PaymentMethodsComponent
            ),
        },
        {
          path: '**',
          redirectTo: '/settings/profile',
        },
      ],
    },
    // TODO: The bottom are generated components that test ssg, ssr, and deferrable views work correctly. Eventually remove them when going to prod, but nice to haves for now.
    {
      path: 'deferrable-views',
      pathMatch: 'full',
      loadComponent: () =>
        import('../pages/deferrable-views/deferrable-views.component').then(
          (c) => c.DeferrableViewsComponent
        ),
    },
    {
      path: 'sweepstakes-rules',
      pathMatch: 'full',
      loadComponent: () =>
        import(
          '../pages/ssg/sweepstakes-rules/sweepstakes-rules.component'
        ).then((c) => c.SweepstakesRulesComponent),
    },
    {
      path: 'privacy-policy',
      pathMatch: 'full',
      loadComponent: () =>
        import('../pages/ssg/privacy-policy/privacy-policy.component').then(
          (c) => c.PrivacyPolicyComponent
        ),
    },
    {
      path: 'terms-and-conditions',
      pathMatch: 'full',
      loadComponent: () =>
        import(
          '../pages/ssg/terms-and-conditions/terms-and-conditions.component'
        ).then((c) => c.TermsAndConditionsComponent),
    },
    {
      path: 'responsible-gaming',
      pathMatch: 'full',
      loadComponent: () =>
        import(
          '../pages/ssg/responsible-gaming/responsible-gaming.component'
        ).then((c) => c.ResponsibleGamingComponent),
    },
    {
      path: 'games',
      pathMatch: 'prefix',
      children: [
        {
          path: '',
          pathMatch: 'full',
          loadComponent: () =>
            import('../pages/games-list/games-list.component').then(
              (c) => c.GamesListComponent
            ),
        },
        {
          path: 'dice',
          pathMatch: 'full',
          loadComponent: () =>
            import('../pages/games/dice/dice.component').then(
              (c) => c.DiceComponent
            ),
        },
        {
          path: 'blackjack',
          pathMatch: 'full',
          loadComponent: () =>
            import('../pages/games/blackjack/blackjack.component').then(
              (c) => c.BlackjackComponent
            ),
        },
      ],
    },
    {
      path: 'groups',
      children: [
        {
          path: ':groupId',
          pathMatch: 'full',
          loadComponent: () =>
            import('../pages/games-list/games-list.component').then(
              (c) => c.GamesListComponent
            ),
        },
      ],
    },
    {
      path: 'providers',
      children: [
        {
          path: ':providerId',
          pathMatch: 'full',
          loadComponent: () =>
            import('../pages/games-list/games-list.component').then(
              (c) => c.GamesListComponent
            ),
        },
      ],
    },
    {
      path: 'vip',
      pathMatch: 'full',
      // Hide the VIP page entirely when the per-user vip kill-switch is off.
      // Redirects home rather than 404ing, matching the other guards.
      canActivate: [killSwitchGuard('vip_enabled')],
      loadComponent: () =>
        import('../pages/vip/vip.component').then((c) => c.VipComponent),
    },
    {
      path: 'referrals',
      pathMatch: 'full',
      canActivate: [authGuardPreservingParams],
      loadComponent: () =>
        import('../pages/referrals/referrals.component').then(
          (c) => c.ReferralsComponent
        ),
    },
    {
      path: 'promotions',
      pathMatch: 'full',
      loadComponent: () =>
        import('../pages/promotions/promotions.component').then(
          (c) => c.PromotionsComponent
        ),
    },
    {
      path: 'provider-games',
      children: [
        {
          path: ':gameId',
          pathMatch: 'full',
          loadComponent: () =>
            import('../pages/provider-games/provider-games.component').then(
              (c) => c.ProviderGamesComponent
            ),
        },
      ],
    },
    {
      path: 'postal-request-code',
      pathMatch: 'full',
      canActivate: [authGuardPreservingParams],
      loadComponent: () =>
        import(
          '../pages/postal-request-code/postal-request-code.component'
        ).then((c) => c.PostalRequestCodeComponent),
    },
    {
      path: 'shop',
      children: [
        {
          path: '',
          pathMatch: 'full',
          redirectTo: shopModalRedirect,
        },
        {
          path: 'orders',
          pathMatch: 'full',
          canActivate: [authGuardPreservingParams, merchModeGuard],
          loadComponent: () =>
            import('../pages/shop/orders/shop-orders.component').then(
              (c) => c.ShopOrdersComponent
            ),
        },
      ],
    },
    {
      path: 'transactions',
      children: [
        {
          path: '',
          pathMatch: 'full',
          redirectTo: 'purchases',
        },
        {
          path: 'purchases',
          pathMatch: 'full',
          canActivate: [authGuardPreservingParams],
          loadComponent: () =>
            import('../pages/transactions/purchases/purchases.component').then(
              (c) => c.PurchasesComponent
            ),
        },
        {
          path: 'redemptions',
          pathMatch: 'full',
          canActivate: [authGuardPreservingParams],
          loadComponent: () =>
            import(
              '../pages/transactions/redemptions/redemptions.component'
            ).then((c) => c.RedemptionsComponent),
        },
      ],
    },
  ],
};
